import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { History, Loader2 } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useLiveDashboard } from '../hooks/useLiveDashboard';
import Card from './Card';

function shortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
}

function fullDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function TrendChartSkeleton() {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-4 h-4 rounded-full bg-gray-200 animate-pulse" />
        <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="h-[200px] bg-gray-100 rounded animate-pulse flex flex-col items-center justify-center gap-2">
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
        <span className="text-xs text-gray-400">Loading assessment history…</span>
      </div>
    </Card>
  );
}

function TrendChartError() {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <History className="w-4 h-4 text-gray-300" />
        <span className="text-sm font-semibold text-gray-800">Assessment History</span>
      </div>
      <div className="h-[200px] flex items-center justify-center text-center px-4">
        <p className="text-xs text-amber-600">
          Assessment history couldn't be loaded. Try refreshing the page.
        </p>
      </div>
    </Card>
  );
}

// Shown when there's exactly one assessment and it has never been reassessed — nothing to
// compare yet. Distinct from "no assessment at all," which this dashboard already handles
// elsewhere via EnterpriseOrganizationAssessmentCard.
function TrendChartEmpty() {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <History className="w-4 h-4 text-gray-300" />
        <span className="text-sm font-semibold text-gray-800">Assessment History</span>
      </div>
      <div className="h-[200px] flex items-center justify-center text-center px-4">
        <p className="text-xs text-gray-400">
          Comparison history will appear here once this assessment has been reassessed at least
          once.
        </p>
      </div>
    </Card>
  );
}

function TrendTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const entry = payload[0]?.payload;
  if (!entry) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <div className="font-semibold text-gray-800 mb-1">{fullDate(entry.date)}</div>
      {entry.isComplete ? (
        <>
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-gray-500">
              <span className="w-2 h-2 rounded-full bg-teal-600 inline-block" /> Risk Count
            </span>
            <span className="font-semibold text-gray-800">{entry.riskCount}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-gray-500">
              <span className="w-2 h-2 rounded-full bg-red-600 inline-block" /> Critical
            </span>
            <span className="font-semibold text-gray-800">{entry.criticalCount}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-gray-500">
              <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" /> High
            </span>
            <span className="font-semibold text-gray-800">{entry.highCount}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-gray-500">
              <span className="w-2 h-2 rounded-full bg-violet-500 inline-block" /> Medium
            </span>
            <span className="font-semibold text-gray-800">{entry.mediumCount}</span>
          </div>
          {entry.topFrameworkScore != null && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-500">Top Framework Score</span>
              <span className="font-semibold text-gray-800">{entry.topFrameworkScore}</span>
            </div>
          )}
          {entry.clickable && (
            <div className="text-teal-600 font-medium mt-1 pt-1 border-t border-gray-100">
              Click to compare
            </div>
          )}
        </>
      ) : (
        <div className="text-amber-600">
          {entry.status === 'analyzing' ? 'Analysis in progress…' : 'Awaiting review submission'}
        </div>
      )}
    </div>
  );
}

// Which two assessments a click on point `index` compares, as { newerId, olderId }, or null when
// that point has nothing to compare with. `points` are chronological (oldest first); `anchorId` is
// the assessment the page is showing. The history holds the whole reassessment chain, so on an
// older assessment's page there are points both before and after it:
//   - the anchor's own point compares it with the previous finalized assessment;
//   - an older point compares the anchor with it, a newer point compares it with the anchor.
// The newer side always comes first, as the compare endpoint requires. When the anchor isn't
// finalized yet (a reassessment still being answered or analyzed), the previous finalized
// assessment stands in for it. Unfinalized points compare with nothing.
export function comparisonFor(points, anchorId, index) {
  const point = points[index];
  if (!point?.isComplete) return null;
  const previousComplete = (i) => {
    for (let j = i - 1; j >= 0; j -= 1) if (points[j].isComplete) return j;
    return -1;
  };
  // anchorId may come from the URL as a string; the history's ids are numbers.
  const anchorIdx = points.findIndex((p) => String(p.assessmentId) === String(anchorId));
  const start = anchorIdx >= 0 ? anchorIdx : points.length - 1;
  const baseIdx = points[start]?.isComplete ? start : previousComplete(start);
  if (baseIdx < 0) return null;
  if (index === baseIdx) {
    const prev = previousComplete(index);
    return prev < 0 ? null : { newerId: point.assessmentId, olderId: points[prev].assessmentId };
  }
  const base = points[baseIdx].assessmentId;
  return index < baseIdx
    ? { newerId: base, olderId: point.assessmentId }
    : { newerId: point.assessmentId, olderId: base };
}

// Status-colored point marker: `color` filled = current, slate filled = historical (clickable),
// hollow slate = not yet finalized. Rendered on top of the gradient-filled area. Two series share
// this chart now, so callers must pass a unique `keyPrefix` to avoid colliding React keys.
// Clicks are handled by the chart, not the dot: the severity lines are drawn over these dots and,
// where the values meet, would otherwise swallow the click.
function TrendDot({ cx, cy, payload, color, keyPrefix, r = 5 }) {
  if (cx == null || cy == null || !payload) return null;
  const notFinalized = !payload.isComplete;
  const fill = notFinalized ? '#ffffff' : payload.isCurrent ? color : '#94a3b8';
  const stroke = notFinalized ? '#94a3b8' : fill;
  return (
    <circle
      key={`${keyPrefix}-${payload.assessmentId ?? payload.idx}`}
      cx={cx}
      cy={cy}
      r={r}
      fill={fill}
      stroke={stroke}
      strokeWidth={2}
      style={{ cursor: payload.clickable ? 'pointer' : 'default' }}
    />
  );
}

// hideWhenEmpty: render nothing (no card, no skeleton) when there's no comparison history —
// an empty "Assessment History" card is just noise. Both current callers (assessment risks
// view, Enterprise Dashboard) pass it; the default empty-state card remains for any future
// caller that wants the feature hint.
export default function AssessmentTrendChart({ assessmentId, hideWhenEmpty = false } = {}) {
  const apiCall = useApi();
  const navigate = useNavigate();
  const [entries, setEntries] = useState(null);
  const [currentAssessmentId, setCurrentAssessmentId] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  // The point under the pointer, so the whole plot shows a pointer cursor over a comparable point.
  const [hoverIdx, setHoverIdx] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      // An explicit assessmentId (e.g. from a deal/BU-scoped risk view) skips the org-level
      // lookup entirely — most orgs' real activity is deal/BU-scoped, not org-level, so a caller
      // that already knows which assessment it's showing should never go through the org-level
      // assessment-summary endpoint, which would resolve to nothing for those orgs.
      let latestId = assessmentId;
      if (!latestId) {
        const summary = await apiCall('GET', '/enterpriseOrganizations/assessment-summary');
        latestId = summary?.latestAssessmentId;
      }
      if (!latestId) {
        setEntries([]);
        setCurrentAssessmentId(null);
        setFetchFailed(false);
        setLoaded(true);
        return;
      }
      setCurrentAssessmentId(latestId);
      const history = await apiCall('GET', `/enterpriseAssessments/${latestId}/history`);
      setEntries(history?.entries ?? []);
      setFetchFailed(false);
    } catch (err) {
      setFetchFailed(true);
    } finally {
      setLoaded(true);
    }
  }, [apiCall, assessmentId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Without this, a bar frozen on "Awaiting review submission"/"Analysis in progress…" at first
  // load never updates to show real numbers once analysis actually finishes minutes later — the
  // same staleness every other live dashboard card already avoids via this hook.
  useLiveDashboard({ refresh: fetchHistory });

  // When the card may end up hidden, don't flash a skeleton that could resolve to nothing —
  // stay invisible until we know there's history to show.
  if (!loaded) return hideWhenEmpty ? null : <TrendChartSkeleton />;
  // hideWhenEmpty is absolute: until real comparison history exists (>= 2 assessments in the
  // chain), the card renders NOTHING — including on a failed history fetch, which previously
  // slipped past the flag and showed a mostly-empty error card on first assessments.
  if (fetchFailed && !entries?.length) return hideWhenEmpty ? null : <TrendChartError />;
  if (!entries || entries.length < 2) return hideWhenEmpty ? null : <TrendChartEmpty />;

  // Entries come back newest-first from the backend; the chart reads left-to-right chronologically.
  const chronological = [...entries].reverse();
  const points = chronological.map((e, i) => {
    const topFrameworkScore = e.frameworkScores?.length
      ? Math.max(...e.frameworkScores.map(f => f.score))
      : null;
    // The API serializes AssessmentStatus as camelCase ("completed", "analyzing", "inProgress"),
    // not PascalCase — matches the same convention EnterpriseAssessment.js already compares
    // against elsewhere in this app.
    const isComplete = e.status === 'completed';
    // Per-severity counts for the Critical/High/Medium trend lines (the API returns riskCounts
    // with lowercase keys). These replaced the old composite 0-100 Severity Score line — the
    // per-tier movement is what reviewers actually act on, and a composite hid e.g. criticals
    // rising while mediums fell.
    const rc = e.riskCounts || {};
    return {
      ...e,
      criticalCount: rc.critical || 0,
      highCount: rc.high || 0,
      mediumCount: rc.medium || 0,
      // Reassessments run same-day often enough that shortDate ("Aug 27") repeats across bars.
      // Recharts' category XAxis resolves hover/tooltip by matching dataKey value, so a duplicate
      // label collapses every bar's hover onto the first match — hence idx as the real axis key,
      // with shortDate only used for the displayed tick text via tickFormatter below.
      idx: i,
      shortDate: shortDate(e.date),
      topFrameworkScore,
      isComplete,
    };
  });
  const chartData = points.map((p, i) => {
    const comparison = comparisonFor(points, currentAssessmentId, i);
    return { ...p, comparison, clickable: !!comparison };
  });

  // The chart's hovered index, from a click or a mouse move (a string or a number in recharts 3).
  const indexOf = (state) => {
    const i = Number(state?.activeTooltipIndex);
    return Number.isInteger(i) && i >= 0 && i < chartData.length ? i : null;
  };
  const handleChartClick = (state) => {
    const comparison = chartData[indexOf(state)]?.comparison;
    if (!comparison) return;
    navigate(`/EnterpriseAssessments/${comparison.newerId}/compare/${comparison.olderId}`);
  };

  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <History className="w-4 h-4 text-teal-600" />
        <span className="text-sm font-semibold text-gray-800">Assessment History</span>
        <span className="text-xs text-gray-400">({chartData.length} assessments)</span>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart
          data={chartData}
          margin={{ top: 12, right: 16, left: 0, bottom: 0 }}
          onClick={handleChartClick}
          onMouseMove={(state) => setHoverIdx(indexOf(state))}
          onMouseLeave={() => setHoverIdx(null)}
          style={{ cursor: chartData[hoverIdx]?.clickable ? 'pointer' : 'default' }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis
            dataKey="idx"
            tick={{ fontSize: 10 }}
            tickFormatter={(i) => chartData[i]?.shortDate ?? ''}
          />
          {/* All four series are risk COUNTS (total + per-severity tier), so they share one axis.
              The old right-hand 0-100 axis existed only for the removed composite Severity Score
              line, which the per-tier Critical/High/Medium lines replaced. */}
          <YAxis yAxisId="risk" tick={{ fontSize: 10 }} width={32} allowDecimals={false} />
          <Tooltip content={<TrendTooltip />} cursor={{ stroke: '#e2e8f0', strokeWidth: 1 }} />
          <Area
            yAxisId="risk"
            type="monotone"
            dataKey="riskCount"
            stroke="#0d9488"
            strokeWidth={2.5}
            fill="none"
            animationDuration={900}
            dot={(props) => (
              <TrendDot {...props} keyPrefix="risk" color="#0d9488" />
            )}
            activeDot={(props) => (
              <TrendDot {...props} keyPrefix="risk-active" color="#0d9488" r={6} />
            )}
          />
          {/* Per-severity trend lines. Plain lines without the TrendDot markers — with four series
              on up to 24 points, per-point dots on every line turn the chart into a scatter; the
              Risk Count area keeps the dots (and the click-to-compare behavior) for the chain. */}
          <Line yAxisId="risk" type="monotone" dataKey="criticalCount" stroke="#dc2626" strokeWidth={2} dot={false} activeDot={{ r: 4 }} animationDuration={900} />
          <Line yAxisId="risk" type="monotone" dataKey="highCount" stroke="#f97316" strokeWidth={2} dot={false} activeDot={{ r: 4 }} animationDuration={900} />
          <Line yAxisId="risk" type="monotone" dataKey="mediumCount" stroke="#8b5cf6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} animationDuration={900} />
        </ComposedChart>
      </ResponsiveContainer>

      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-teal-600 inline-block" /> Risk Count
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-600 inline-block" /> Critical
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-orange-500 inline-block" /> High
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-violet-500 inline-block" /> Medium
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-white border-2 border-slate-400 inline-block" /> Hollow = not yet finalized
        </span>
      </div>
    </Card>
  );
}
